import React, {useEffect, useState} from "react";
import { Link } from "react-router-dom";
import { Modal, ControlLabel, FormControl, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { View } from "react-native";
import { Checkmark } from 'react-checkmark';
import {
    getAllBuilders, 
    getAllNeighborhoods,
    patchCapBuilder,
    patchCapHealth,
    changeCapNeighborhood,
    patchCapStatus,
    patchCapBandwidth,
    patchCapArchitecture,
    getArchitectures,
    getExpectedBatteryEmails,
    triggerBatteryEmail,
    createNeighborhood,
    setTimelapseSettings
} from "../utils/bedrockAPI";
import { getUser } from "../utils/Common";

const HEALTH_OPTIONS = ['POWER_LOSS','ERROR','BATTERY','ONLINE'];
const STATUS_OPTIONS = ['ACTIVE','BUILD','DECOMISSIONED','DELETE'];
const BANDWIDTH_OPTIONS = ['HIGH','LOW'];
const TIMELAPSE_INTERVALS = [15, 60];

export default function CapModal({show,cap,setShowCapModal,...props}) {
    const [builders,setBuilders] = useState(null);
    const [currentBuilder,setCurrentBuilder] = useState(null);
    const [neighborhoods,setNeighborhoods] = useState(null);
    const [currentNeigborhood,setCurrentNeighborhood] = useState(null);
    const [capHealth,setCapHealth] = useState(cap ? cap.health : "POWER_LOSS");
    const [capStatus,setCapStatus] = useState(cap ? cap.status : "ACTIVE");
    const [bandwidth,setBandwidth] = useState(cap ? cap.bandwidth || "HIGH" : "HIGH");
    const [architectures,setArchitectures] = useState(null);
    const [capArchitecture,setCapArchitecture] = useState(cap ? cap.architecture || "" : "");
    const [timelapseInterval,setTimelapseInterval] = useState(null);
    const [expectedBatteryEmails,setExpectedBatteryEmails] = useState(null);
    const [showBatteryConfirmation, setShowBatteryConfirmation] = useState(false);
    const [isSendingEmail, setIsSendingEmail] = useState(false);
    const [emailSent, setEmailSent] = useState(false);
    const [showNewNeighborhoodModal, setShowNewNeighborhoodModal] = useState(false);
    const [newNeighborhoodName, setNewNeighborhoodName] = useState('');
    const [isCreatingNeighborhood, setIsCreatingNeighborhood] = useState(false);
    const [neighborhoodCreated, setNeighborhoodCreated] = useState(false);
    const [modalMode, setModalMode] = useState('edit'); // 'edit' | 'battery' | 'newNeighborhood'

    useEffect(() => {
        // console.log(cap);
        setCapHealth(cap.health);
        setCapStatus(cap.status);
        setBandwidth(cap.bandwidth || "HIGH");
        setCapArchitecture(cap.architecture || "");
        setTimelapseInterval(cap.timelapse && cap.timelapse.interval ? cap.timelapse.interval : null);
        async function accessExpectedBatteryEmails() {
            const tempExpectedBatteryEmails = await getExpectedBatteryEmails(cap.capId);
            // console.log(tempExpectedBatteryEmails);
            // Filter out admin@bedrockwireless.com
            const filteredEmails = tempExpectedBatteryEmails.filter(user => user.email !== 'admin@bedrockwireless.com');
            setExpectedBatteryEmails(filteredEmails);
        }
        if(getUser().role==="ADMIN") {
            accessExpectedBatteryEmails();
        }
        // console.log(cap.status);
    },[cap]);

    useEffect(() => {
        async function accessBuilders() {
            const tempBuilders = await getAllBuilders();
            let tempBuilderObject = {}
            for(var i=0; i<tempBuilders.length; i++) {
                tempBuilderObject[tempBuilders[i].builderId] = tempBuilders[i];
            }
            tempBuilderObject[null] = {builderId:null,name:"NO BUILDER"};
            setBuilders(tempBuilderObject);
        }
        async function accessNeighborhoods() {
            const tempNeighborhoods = await getAllNeighborhoods();
            let tempNeighborhoodObject = {}
            for(var i=0; i<tempNeighborhoods.length; i++) {
                tempNeighborhoodObject[tempNeighborhoods[i].neighborhoodId] = tempNeighborhoods[i];
            }
            tempNeighborhoodObject[null] = {neighborhoodId:null,name:"NO NEIGHBORHOOD"};
            setNeighborhoods(tempNeighborhoodObject);
        }
        async function accessArchitectures() {
            const tempArchitectures = await getArchitectures();
            setArchitectures(tempArchitectures);
        }
        if(show) {
            accessBuilders();
            accessNeighborhoods();
            accessArchitectures();
        }
    },[show]);

    useEffect(() => {
        if (!show) {
            setCurrentBuilder(null);
            setCurrentNeighborhood(null);
            return;
        }
        const capBuilderId = cap.builderId ?? cap.builderid ?? null;
        const capNeighborhoodId = cap.neighborhoodId ?? cap.neighborhoodid ?? null;
        if (builders && currentBuilder === null) {
            setCurrentBuilder(builders[capBuilderId] ?? null);
        }
        if (neighborhoods && currentNeigborhood === null) {
            setCurrentNeighborhood(neighborhoods[capNeighborhoodId] ?? null);
        }
    }, [show, builders, neighborhoods, cap, currentBuilder, currentNeigborhood])

    function updateBuilder(e) {
        const builderId = e.target.value === "null" ? null : e.target.value;
        if (builders && builders[builderId] !== undefined) {
            setCurrentBuilder(builders[builderId]);
        }
    }

    function updateNeighborhood(e) {
        const neighborhoodId = e.target.value === "null" ? null : e.target.value;
        if (neighborhoods && neighborhoods[neighborhoodId] !== undefined) {
            setCurrentNeighborhood(neighborhoods[neighborhoodId]);
        }
    }

    async function updateCapHealth(e) {
        setCapHealth(e.target.value);
    }

    async function updateCapArchitecture(e) {
        setCapArchitecture(e.target.value);
    }

    async function updateTimelapseInterval(e) {
        const value = e.target.value;
        setTimelapseInterval(value === "" ? null : parseInt(value));
    }

    async function modifyCapAndBuilder(e) {
        e.preventDefault();
        const capBuilderId = cap.builderId ?? cap.builderid ?? null;
        const capNeighborhoodId = cap.neighborhoodId ?? cap.neighborhoodid ?? null;
        const tasks = [];
        if(currentBuilder.builderId!==capBuilderId) {
            tasks.push(patchCapBuilder(cap.capId,currentBuilder.builderId));
        }
        if(currentNeigborhood.neighborhoodId!==capNeighborhoodId) {
            console.log(cap.capId,currentNeigborhood.neighborhoodId)
            tasks.push(changeCapNeighborhood(cap.capId,currentNeigborhood.neighborhoodId));
        }
        if(capHealth!==cap.health) {
            tasks.push(patchCapHealth(cap.capId,capHealth));
        }
        if(capStatus!==cap.status) {
            tasks.push(patchCapStatus(cap.capId,capStatus));
        }
        if(bandwidth!==cap.bandwidth) {
            tasks.push(patchCapBandwidth(cap.capId,bandwidth));
        }
        if(capArchitecture!==cap.architecture) {
            tasks.push(patchCapArchitecture(cap.capId,capArchitecture));
        }
        // Set timelapse settings only if user has specified a value
        if(timelapseInterval !== null) {
            tasks.push(setTimelapseSettings(cap.capId, timelapseInterval));
        }
        if (tasks.length > 0) {
            await Promise.all(tasks);
        }
        setShowCapModal(false);
    }

    const handleTriggerBatteryEmail = async () => {
        setIsSendingEmail(true);
        try {
            await triggerBatteryEmail(cap.capId);
            setEmailSent(true);
            // Reset states after 2 seconds
            setTimeout(() => {
                setShowBatteryConfirmation(false);
                setEmailSent(false);
                setIsSendingEmail(false);
            }, 2000);
        } catch (error) {
            console.error('Error sending battery email:', error);
            setIsSendingEmail(false);
        }
        setShowCapModal(false);
    };

    const handleCreateNeighborhood = async () => {
        if (!newNeighborhoodName.trim()) return;
        
        setIsCreatingNeighborhood(true);
        try {
            const newNeighborhood = await createNeighborhood(newNeighborhoodName.trim());
            // Refresh neighborhoods list
            const tempNeighborhoods = await getAllNeighborhoods();
            let tempNeighborhoodObject = {}
            for(var i=0; i<tempNeighborhoods.length; i++) {
                tempNeighborhoodObject[tempNeighborhoods[i].neighborhoodId] = tempNeighborhoods[i];
            }
            tempNeighborhoodObject[null] = {neighborhoodId:null,name:"NO NEIGHBORHOOD"};
            setNeighborhoods(tempNeighborhoodObject);
            
            setNeighborhoodCreated(true);
            // Reset states and return to edit mode after 2 seconds
            setTimeout(() => {
                setModalMode('edit');
                setNewNeighborhoodName('');
                setNeighborhoodCreated(false);
                setIsCreatingNeighborhood(false);
            }, 2000);
        } catch (error) {
            console.error('Error creating neighborhood:', error);
            setIsCreatingNeighborhood(false);
        }
    };

    const renderModalContent = () => {
        switch (modalMode) {
            case 'battery':
                return (
                    <>
                        <Modal.Header closeButton>
                            <Modal.Title>Confirm Battery Email</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <p>The following users ({expectedBatteryEmails.length}) will receive a battery email:</p>
                            {expectedBatteryEmails && expectedBatteryEmails.length > 0 ? (
                                <ul>
                                    {expectedBatteryEmails.map((user, index) => (
                                        <li key={index}>{user.email}</li>
                                    ))}
                                </ul>
                            ) : (
                                <p>No users will receive the email.</p>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button onClick={() => setModalMode('edit')}>Cancel</Button>
                            <Button 
                                bsStyle="warning" 
                                onClick={handleTriggerBatteryEmail}
                                disabled={isSendingEmail}
                            >
                                {isSendingEmail ? (
                                    <span>
                                        <i className="fa fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>
                                        Sending...
                                    </span>
                                ) : emailSent ? (
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <Checkmark size="large" />
                                    </span>
                                ) : (
                                    'Send Email'
                                )}
                            </Button>
                        </Modal.Footer>
                    </>
                );

            case 'newNeighborhood':
                return (
                    <>
                        <Modal.Header closeButton>
                            <Modal.Title>Create New Neighborhood</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <FormControl
                                type="text"
                                placeholder="Enter neighborhood name"
                                value={newNeighborhoodName}
                                onChange={(e) => setNewNeighborhoodName(e.target.value)}
                                disabled={isCreatingNeighborhood}
                            />
                        </Modal.Body>
                        <Modal.Footer>
                            <Button onClick={() => setModalMode('edit')}>Cancel</Button>
                            <Button 
                                bsStyle="primary" 
                                onClick={handleCreateNeighborhood}
                                disabled={isCreatingNeighborhood || !newNeighborhoodName.trim()}
                            >
                                {isCreatingNeighborhood ? (
                                    <span>
                                        <i className="fa fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>
                                        Creating...
                                    </span>
                                ) : neighborhoodCreated ? (
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <Checkmark size="large" />
                                    </span>
                                ) : (
                                    'Create'
                                )}
                            </Button>
                        </Modal.Footer>
                    </>
                );

            default: // 'edit'
                return (
                    <>
                        <Modal.Header closeButton>
                            <Modal.Title>
                                Edit CAP metadata
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {builders && 
                            currentBuilder && 
                            neighborhoods && 
                            currentNeigborhood && 
                            HEALTH_OPTIONS && 
                            capHealth && (
                                <form onSubmit={(e) => modifyCapAndBuilder(e)}>
                                    <View style={{flex:1,flexDirection:"row",justifyContent:"flex-end",paddingBottom:"5px"}}>
                                        <ControlLabel style={{width:"200px",paddingRight:"10px",paddingTop:"15px"}}>
                                            Builder
                                        </ControlLabel>
                                        <div style={{ display: 'flex', gap: '10px', flex: 1 }}>
                                            <FormControl
                                                componentClass="select"
                                                value={currentBuilder.builderId ?? "null"}
                                                onChange={(e) => updateBuilder(e)}
                                                style={{height:"50px"}}
                                            >
                                                {builders && (Object.keys(builders)).map((builderId) => (
                                                    <option key={builderId} value={builderId}>
                                                        {builders[builderId].name}
                                                    </option>
                                                ))}
                                            </FormControl>
                                        </div>
                                    </View>
                                    <View style={{flex:1,flexDirection:"row",justifyContent:"flex-end",paddingBottom:"5px"}}>
                                        <ControlLabel style={{width:"200px",paddingRight:"10px",paddingTop:"15px"}}>
                                            Neighborhood
                                        </ControlLabel>
                                        <div style={{ display: 'flex', gap: '10px', flex: 1 }}>
                                            <FormControl
                                                componentClass="select"
                                                value={currentNeigborhood.neighborhoodId ?? "null"}
                                                onChange={(e) => updateNeighborhood(e)}
                                                style={{height:"50px"}}
                                            >
                                                {neighborhoods && (Object.keys(neighborhoods)).map((neighborhoodId) => (
                                                    <option key={neighborhoodId} value={neighborhoodId}>
                                                        {neighborhoods[neighborhoodId].name}
                                                    </option>
                                                ))}
                                            </FormControl>
                                            <Button 
                                                bsStyle="primary"
                                                onClick={() => setModalMode('newNeighborhood')}
                                                style={{ height: "50px", width: "50px" }}
                                            >
                                                <i className="fa fa-plus" />
                                            </Button>
                                        </div>
                                    </View>
                                    <View style={{flex:1,flexDirection:"row",justifyContent:"flex-end",paddingBottom:"5px"}}>
                                        <OverlayTrigger placement="right" overlay={(
                                            <Tooltip id="warningHealth">
                                                DO NOT CHANGE UNLESS YOU KNOW WHAT YOU'RE DOING
                                            </Tooltip>
                                        )}>
                                            <ControlLabel style={{width:"200px",paddingRight:"10px",paddingTop:"15px"}}>
                                                Cap Health 
                                            </ControlLabel>
                                        </OverlayTrigger>
                                        <div style={{ display: 'flex', gap: '10px', flex: 1 }}>
                                            <FormControl
                                                componentClass="select"
                                                defaultValue={capHealth}
                                                onChange={(e) => updateCapHealth(e)}
                                                style={{height:"50px"}}
                                            >
                                                {HEALTH_OPTIONS.map((health,i) => (
                                                    <option key={i}>
                                                        {health}
                                                    </option>
                                                ))}
                                            </FormControl>
                                        </div>
                                    </View>
                                    <View style={{flex:1,flexDirection:"row",justifyContent:"flex-end",paddingBottom:"5px"}}>
                                        <OverlayTrigger placement="right" overlay={(
                                            <Tooltip id="statusHelp">
                                                The STATUS can be one of three things:<br />
                                                -ACTIVE (activated caps in use by a customer)<br />
                                                -BUILD (built CAP that's not yet activated)<br />
                                                -DECOMISSIONED (CAPs that aren't in use - yes I spelled it wrong)<br />
                                                DO NOT CHANGE THIS UNLESS YOU KNOW WHAT YOU'RE DOING
                                            </Tooltip>
                                        )}>
                                            <ControlLabel style={{width:"200px",paddingRight:"10px",paddingTop:"15px"}}>
                                                Cap Status 
                                            </ControlLabel>
                                        </OverlayTrigger>
                                        <div style={{ display: 'flex', gap: '10px', flex: 1 }}>
                                            <FormControl
                                                componentClass="select"
                                                defaultValue={capStatus}
                                                onChange={(e) => setCapStatus(e.target.value)}
                                                style={{height:"50px"}}
                                            >
                                                {STATUS_OPTIONS.map((status,i) => (
                                                    <option key={i}>
                                                        {status}
                                                    </option>
                                                ))}
                                            </FormControl>
                                        </div>
                                    </View>
                                    <View style={{flex:1,flexDirection:"row",justifyContent:"flex-end",paddingBottom:"5px"}}>
                                        <ControlLabel style={{width:"200px",paddingRight:"10px",paddingTop:"15px"}}>
                                            Cap Bandwdith
                                        </ControlLabel>
                                        <div style={{ display: 'flex', gap: '10px', flex: 1 }}>
                                            <FormControl
                                                componentClass="select"
                                                defaultValue={bandwidth}
                                                onChange={(e) => setBandwidth(e.target.value)}
                                                style={{height:"50px"}}
                                            >
                                                {BANDWIDTH_OPTIONS.map((band,i) => (
                                                    <option key={i}>
                                                        {band}
                                                    </option>
                                                ))}
                                            </FormControl>
                                        </div>
                                    </View>
                                    <View style={{flex:1,flexDirection:"row",justifyContent:"flex-end",paddingBottom:"5px"}}>
                                        <OverlayTrigger placement="right" overlay={(
                                            <Tooltip id="architectureHelp">
                                                The ARCHITECTURE determines the camera setup and capabilities.<br />
                                                DO NOT CHANGE THIS UNLESS YOU KNOW WHAT YOU'RE DOING
                                            </Tooltip>
                                        )}>
                                            <ControlLabel style={{width:"200px",paddingRight:"10px",paddingTop:"15px"}}>
                                                Cap Architecture 
                                            </ControlLabel>
                                        </OverlayTrigger>
                                        <div style={{ display: 'flex', gap: '10px', flex: 1 }}>
                                            <FormControl
                                                componentClass="select"
                                                defaultValue={capArchitecture}
                                                onChange={(e) => updateCapArchitecture(e)}
                                                style={{height:"50px"}}
                                            >
                                                {architectures && architectures.map((arch,i) => (
                                                    <option key={i}>
                                                        {arch}
                                                    </option>
                                                ))}
                                            </FormControl>
                                        </div>
                                    </View>
                                    {cap.unitId && (
                                    <View style={{flex:1,flexDirection:"row",justifyContent:"flex-end",paddingBottom:"5px"}}>
                                        <ControlLabel style={{width:"200px",paddingRight:"10px",paddingTop:"15px"}}>
                                            JobSight unit
                                        </ControlLabel>
                                        <div style={{ display: 'flex', gap: '10px', flex: 1, alignItems: 'center' }}>
                                            <Link
                                                to={`/admin/units/${cap.unitId}`}
                                                onClick={() => setShowCapModal(false)}
                                                style={{fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace", fontWeight: 600, letterSpacing: "0.04em"}}
                                            >
                                                {String(cap.unitId).slice(0, 8).toUpperCase()}
                                            </Link>
                                        </div>
                                    </View>
                                    )}
                                    <View style={{flex:1,flexDirection:"row",justifyContent:"flex-end",paddingBottom:"5px"}}>
                                        <ControlLabel style={{width:"200px",paddingRight:"10px",paddingTop:"15px"}}>
                                            Timelapse Interval (minutes)
                                        </ControlLabel>
                                        <div style={{ display: 'flex', gap: '10px', flex: 1 }}>
                                            <FormControl
                                                componentClass="select"
                                                value={timelapseInterval || ""}
                                                onChange={(e) => updateTimelapseInterval(e)}
                                                style={{height:"50px"}}
                                            >
                                                <option value="">No Additional Timelapse</option>
                                                {TIMELAPSE_INTERVALS.map((interval,i) => (
                                                    <option key={i} value={interval}>
                                                        {interval}
                                                    </option>
                                                ))}
                                            </FormControl>
                                        </div>
                                    </View>
                                    <View style={{flex:1,flexDirection:"row",justifyContent:"space-between",paddingTop:"10px",gap:"10px"}}>
                                        <Button
                                            type="button"
                                            bsStyle="warning"
                                            bsSize="large"
                                            onClick={() => setModalMode('battery')}
                                        >
                                            Send Battery Email
                                        </Button>
                                        <Button
                                            type="submit"
                                            bsStyle="info"
                                            bsSize="large"
                                            disabled={
                                                currentBuilder.builderId===(cap.builderId ?? cap.builderid ?? null) && 
                                                currentNeigborhood.neighborhoodId===(cap.neighborhoodId ?? cap.neighborhoodid ?? null) &&
                                                capHealth===cap.health &&
                                                capStatus===cap.status &&
                                                bandwidth===(cap.bandwidth || "HIGH") &&
                                                capArchitecture===(cap.architecture || "") &&
                                                timelapseInterval===(cap.timelapse?.interval ?? null)
                                            }
                                        >
                                            Save
                                        </Button>
                                    </View>
                                </form>
                            )}
                        </Modal.Body>
                        {props.children}
                    </>
                );
        }
    };

    return (
        <Modal
            show={show}
            onHide={() => {
                setShowCapModal(false);
                setModalMode('edit');
            }}
            container={this}
            aria-labelledby="contained-modal-title"
            {...props}
        >
            {renderModalContent()}
        </Modal>
    );
}